import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, Save, History, Trash2, RefreshCw, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const relTime = (iso) => {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const minutes = Math.round((Date.now() - ts) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * Versioned defaults controls — three buttons + a history dialog.
 *
 * 1. "Save as default"  → POST /api/admin/snapshots/{section} (creates a NEW snapshot)
 * 2. "Re-seed defaults" → POST /api/admin/seed-{section} (restores the LATEST snapshot)
 * 3. "Versions"         → opens a dialog listing all snapshots; restore/delete each
 *
 * Below the buttons we render the latest-snapshot meta line ("Last saved 3m ago by …").
 */
const DefaultsControls = ({
  section,
  reseedConfirm,
  onComplete,
  reseedTestId,
  snapshotTestId,
  versionsTestId = `versions-${section}-btn`,
}) => {
  const [busy, setBusy] = useState(null); // 'reseed' | 'snapshot' | null
  const [snapshots, setSnapshots] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyBusyId, setHistoryBusyId] = useState(null);

  const token = () => localStorage.getItem('token');
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const fetchSnapshots = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/snapshots/${section}`, auth());
      setSnapshots(res.data?.snapshots || []);
    } catch {
      setSnapshots([]);
    }
  };

  useEffect(() => {
    fetchSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const latest = snapshots[0] || null;

  // --- Save current as a NEW snapshot (with optional name) ---
  const handleSnapshot = async () => {
    const name = window.prompt(
      `Name this version of "${section.replace('-', ' ')}" (optional):\n\n` +
      'Tip: try "Pre-launch baseline", "Holiday content", "v2 with new copy", etc.',
      '',
    );
    if (name === null) return; // user cancelled
    setBusy('snapshot');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/snapshots/${section}`,
        { name: name.trim() || null },
        auth(),
      );
      const n = res.data?.items_snapshotted ?? '?';
      toast.success(`Saved "${res.data?.name || 'snapshot'}" with ${n} item${n === 1 ? '' : 's'}.`);
      fetchSnapshots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save snapshot');
    } finally {
      setBusy(null);
    }
  };

  // --- Re-seed = restore the LATEST snapshot (or factory) ---
  const handleReseed = async () => {
    if (!window.confirm(reseedConfirm)) return;
    setBusy('reseed');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/seed-${section}`, {}, auth());
      const d = res.data || {};
      const src = d.source === 'snapshot' ? `"${d.snapshot_name || 'latest snapshot'}"` : 'factory defaults';
      if ('inserted' in d) {
        toast.success(`Re-seeded ${d.inserted} item${d.inserted === 1 ? '' : 's'} from ${src}.`);
      } else {
        toast.success(`Restored from ${src}.`);
      }
      if (onComplete) onComplete(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to re-seed defaults');
    } finally {
      setBusy(null);
    }
  };

  // --- Restore a SPECIFIC snapshot from the history ---
  const handleRestore = async (snap) => {
    if (!window.confirm(
      `Restore "${snap.name}"?\n\nThis will overwrite the current ${section.replace('-', ' ')} content.`
    )) return;
    setHistoryBusyId(snap.id);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/snapshots/${section}/${snap.id}/restore`,
        {},
        auth(),
      );
      const inserted = res.data?.inserted;
      if (typeof inserted === 'number') {
        toast.success(`Restored "${snap.name}" — ${inserted} item${inserted === 1 ? '' : 's'} live.`);
      } else {
        toast.success(`Restored "${snap.name}".`);
      }
      if (onComplete) onComplete(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore snapshot');
    } finally {
      setHistoryBusyId(null);
    }
  };

  const handleDelete = async (snap) => {
    if (!window.confirm(`Delete the snapshot "${snap.name}" permanently? This cannot be undone.`)) return;
    setHistoryBusyId(snap.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/snapshots/${section}/${snap.id}`, auth());
      toast.success(`Deleted "${snap.name}".`);
      fetchSnapshots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete snapshot');
    } finally {
      setHistoryBusyId(null);
    }
  };

  return (
    <div className="inline-flex flex-col items-end gap-1">
      <div className="inline-flex items-center gap-2 flex-wrap">
        <Button
          onClick={handleSnapshot}
          disabled={!!busy}
          variant="outline"
          className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-2"
          data-testid={snapshotTestId}
          title="Save the current content as a new named version"
        >
          <Save size={16} className={busy === 'snapshot' ? 'animate-pulse' : ''} />
          {busy === 'snapshot' ? 'Saving…' : 'Save as default'}
        </Button>
        <Button
          onClick={handleReseed}
          disabled={!!busy}
          variant="outline"
          className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-2"
          data-testid={reseedTestId}
          title="Restore the most recent saved snapshot"
        >
          <RotateCcw size={16} className={busy === 'reseed' ? 'animate-spin' : ''} />
          {busy === 'reseed' ? 'Re-seeding…' : 'Re-seed defaults'}
        </Button>
        <Button
          onClick={() => { fetchSnapshots(); setShowHistory(true); }}
          variant="outline"
          className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-2"
          data-testid={versionsTestId}
          title="View, restore or delete previous versions"
        >
          <History size={16} />
          Versions
          {snapshots.length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-terracotta/15 text-terracotta text-[11px] font-semibold">
              {snapshots.length}
            </span>
          )}
        </Button>
      </div>
      {latest ? (
        <p className="text-[11px] text-maroon/50 italic" data-testid={`defaults-meta-${section}`}>
          Last saved &quot;{latest.name}&quot; {relTime(latest.created_at)}
          {latest.created_by ? ` by ${latest.created_by}` : ''}
          {typeof latest.items_count === 'number' ? ` • ${latest.items_count} item${latest.items_count === 1 ? '' : 's'}` : ''}
        </p>
      ) : (
        <p className="text-[11px] text-maroon/40 italic" data-testid={`defaults-meta-${section}`}>
          No saved snapshots yet — click &quot;Save as default&quot; to lock in your current content.
        </p>
      )}

      {/* Version history dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-maroon">
              <History size={20} />
              Version history — {section.replace('-', ' ')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-maroon/60">
              {snapshots.length === 0 ? 'No saved snapshots yet.' : `${snapshots.length} saved snapshot${snapshots.length === 1 ? '' : 's'} (newest first).`}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-1"
              onClick={fetchSnapshots}
              data-testid={`refresh-versions-${section}-btn`}
            >
              <RefreshCw size={14} /> Refresh
            </Button>
          </div>
          <ul className="space-y-2">
            {snapshots.map((snap) => (
              <li
                key={snap.id}
                className="border border-maroon/15 rounded-sm p-3 flex items-start justify-between gap-3 hover:bg-sand/30 transition-colors"
                data-testid={`version-row-${snap.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-maroon truncate">{snap.name}</p>
                    {snap.is_latest && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-terracotta/15 text-terracotta px-1.5 py-0.5 rounded-sm">
                        <Star size={10} /> Latest
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-maroon/60 mt-0.5">
                    {relTime(snap.created_at)} • {snap.items_count} item{snap.items_count === 1 ? '' : 's'}
                    {snap.created_by ? ` • ${snap.created_by}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-maroon/30 text-maroon hover:bg-maroon/5"
                    disabled={historyBusyId === snap.id}
                    onClick={() => handleRestore(snap)}
                    data-testid={`restore-version-${snap.id}-btn`}
                  >
                    <RotateCcw size={14} className={historyBusyId === snap.id ? 'animate-spin mr-1' : 'mr-1'} />
                    Restore
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    disabled={historyBusyId === snap.id}
                    onClick={() => handleDelete(snap)}
                    data-testid={`delete-version-${snap.id}-btn`}
                    aria-label={`Delete ${snap.name}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DefaultsControls;
